// Componente principal para gerenciamento de convites e vínculos entre usuários
import { useSelector } from "react-redux";
import Main from "../../../hoc/Layout/Main";
import MainTitleSearch from "../../../components/UI/MainTitle/MainTitlePurple";
import TagCard from "../../../components/Cards";
import classes from "./RescueCode.module.scss";
import { useInviteCode } from "./hooks/useInviteCode";
import { STATES } from './utils/constants';
import ProfileUpdateModal from "../../../components/ProfileUpdateModal/ProfileUpdateModal";

// Importação de todos os componentes de visualização de estado
import LoadingView from "./components/StateViews/LoadingView";
import AlreadyLinkedView from "./components/StateViews/AlreadyLinkedView";
import InvalidCodeView from "./components/StateViews/InvalidCodeView";
import NoInviteView from "./components/StateViews/NoInviteView";
import HasLinkView from "./components/StateViews/HasLinkView";
import NoLinkView from "./components/StateViews/NoLinkView";
import SuccessView from "./components/StateViews/SuccessView";
import RejectedView from "./components/StateViews/RejectedView";
import UnlinkProfileView from "./components/StateViews/UnlinkProfileView";
import UnlinkSuccessView from "./components/StateViews/UnlinkSuccessView";
import UnlinkErrorView from "./components/StateViews/UnlinkErrorView";
import ProfessionalBlockedView from "./components/StateViews/ProfessionalBlockedView";
import SelfInviteBlockedView from "./components/StateViews/SelfInviteBlockedView";

/**
 * Componente RescueCode
 * 
 * Este componente gerencia todo o fluxo de vinculação entre usuários e profissionais.
 * Funciona como uma máquina de estados, onde cada estado representa uma etapa diferente
 * do processo de vinculação (convite recebido, já vinculado, erro, etc).
 * 
 * A lógica de negócio foi extraída para o hook useInviteCode, deixando este componente
 * responsável apenas pela renderização da interface com base no estado atual.
 */
const RescueCode = () => {
  // Hook personalizado que centraliza toda a lógica de convites e vinculação
  // Retorna o estado atual e funções para manipulá-lo
  const {
    state,
    updateState,
    redirectToDashboard,
    onGenerateNewLink,
    handleProfileUpdated,
    confirmInvitation,
    rejectInvitation,
    showUnlinkScreen,
    confirmUnlink,
    cancelUnlinkConfirm,
    isProfileModalOpen,
    openProfileModal,
    closeProfileModal,
    updateProfile,
    currentProfile,
    isLoading,
  } = useInviteCode();

  // Mapeamento entre estados e componentes de visualização correspondentes
  // Cada estado da máquina tem um componente visual específico
  const stateComponents = {
    // Estado: Usuário já está vinculado com o mesmo profissional
    [STATES.ALREADY_LINKED]: () => (
      <AlreadyLinkedView 
        inviterName={state.inviterName}
        redirectToDashboard={redirectToDashboard}
      />
    ),
    // Estado: Código do convite é inválido ou expirou
    [STATES.INVALID_CODE]: () => (
      <InvalidCodeView 
        errorMessage={state.errorMessage}
        inviterName={state.inviterName}
        redirectToDashboard={redirectToDashboard}
      />
    ),
    // Estado: Nenhum convite encontrado (mostra opção de gerenciar vínculo atual)
    [STATES.NO_INVITE]: () => (
      <NoInviteView 
        currentParentUser={state.currentParentUser}
        redirectToDashboard={redirectToDashboard}
        showUnlinkScreen={showUnlinkScreen}
        updateState={updateState}  // Adicionada esta propriedade
      />
    ),
    // Estado: Usuário já tem vínculo com outro profissional (precisa confirmar a troca)
    [STATES.HAS_LINK]: () => (
      <HasLinkView 
        currentParentUser={state.currentParentUser}
        newParentUser={state.newParentUser}
        confirmInvitation={confirmInvitation}
        rejectInvitation={rejectInvitation}
      />
    ),
    // Estado: Usuário não tem vínculo e recebeu um convite
    [STATES.NO_LINK]: () => (
      <NoLinkView 
        newParentUser={state.newParentUser}
        confirmInvitation={confirmInvitation}
        rejectInvitation={rejectInvitation}
      />
    ),
    // Estado: Vinculação concluída com sucesso
    [STATES.SUCCESS]: () => (
      <SuccessView 
        newParentUser={state.newParentUser}
        redirectToDashboard={redirectToDashboard}
      />
    ),
    // Estado: Convite foi recusado pelo usuário
    [STATES.REJECTED]: () => (
      <RejectedView 
        rejectedUserName={state.rejectedUserName}
        newParentUser={state.newParentUser}
        redirectToDashboard={redirectToDashboard}
      />
    ),
    // Estado: Tela para desvincular o usuário atual
    [STATES.UNLINK_PROFILE]: () => (
      <UnlinkProfileView 
        currentParentUser={state.currentParentUser}
        unlinkInput={state.unlinkInput}
        unlinkError={state.unlinkError}
        showUnlinkConfirm={state.showUnlinkConfirm}
        updateState={updateState}
        confirmUnlink={confirmUnlink}
        cancelUnlinkConfirm={cancelUnlinkConfirm}
        redirectToDashboard={redirectToDashboard}
      />
    ),
    // Estado: Desvinculação concluída com sucesso
    [STATES.UNLINK_SUCCESS]: () => (
      <UnlinkSuccessView 
        redirectToDashboard={redirectToDashboard}
      />
    ),
    // Estado: Erro na desvinculação
    [STATES.UNLINK_ERROR]: () => (
      <UnlinkErrorView 
        updateState={updateState}
        redirectToDashboard={redirectToDashboard}
      />
    ),
    // Estado: Profissional tentando se vincular a outro profissional
    [STATES.PROFESSIONAL_BLOCKED]: () => (
      <ProfessionalBlockedView
        redirectToDashboard={redirectToDashboard}
        redirectToAccountSettings={openProfileModal}
      />
    ),
    // Estado: Usuário tentando se vincular a si mesmo
    [STATES.SELF_INVITE_BLOCKED]: () => (
      <SelfInviteBlockedView
        redirectToDashboard={redirectToDashboard}
        onGenerateNewLink={onGenerateNewLink}
      />
    ),
    // Estado padrão: Carregando
    [STATES.LOADING]: () => <LoadingView />
  };

  // Exibe o componente de carregamento enquanto os dados estão sendo buscados
  // Isso evita mostrar telas vazias ou incompletas para o usuário
  if (isLoading) {
    return (
      <div className={classes.rescueCodeContainer}>
        <MainTitleSearch
          full
          hasBG
          title="Convite de Vínculo"
        />
        <Main full>
          <div className={classes.contentWrapper}>
            <TagCard className={classes.rescueCodeCard}>
              <LoadingView />
            </TagCard>
          </div>
        </Main>
      </div>
    );
  }

  // Seleciona o componente correto com base no estado atual
  // Se o estado for desconhecido, usa o componente de carregamento como fallback
  const CurrentStateComponent = stateComponents[state.current] || stateComponents[STATES.LOADING];

  // Renderização da estrutura principal do componente
  // O conteúdo interno muda de acordo com o estado atual (CurrentStateComponent)
  return (
    <div className={classes.rescueCodeContainer}>
      <MainTitleSearch
        full
        hasBG
        title="Convite de Vínculo"
      />
      <Main full>
        <div className={classes.contentWrapper}>
          <TagCard className={classes.rescueCodeCard}>
            <CurrentStateComponent />
          </TagCard>
        </div>
      </Main>
      <ProfileUpdateModal
        open={isProfileModalOpen}
        onClose={closeProfileModal}
        currentProfile={currentProfile}
        onProfileUpdate={updateProfile}
        onFinish={handleProfileUpdated}
      />
    </div>
  );
};

export default RescueCode;